import React, { useState, useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";

import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import {
  fetchAssignedPlots,
  fetchZones,
  fetchProjects,
  fetchWaterSupplyTypes,
  fetchMaintenanceTypes,
  fetchAllUsers,
  fetchStaff,
  assignPlotSupervisor,
  assignPlotOwner,
  assignPlotZone,
  fetchZohoUpdateStatus,
  fetchPlotMaintenanceHistory,
} from "../api/api";

const PAGE_SIZE = 25;

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Plans that are never billed/tracked as "due" — a plot on one of these has
// no meaningful Active/Inactive maintenance status, so it's shown as N/A
// rather than misleadingly Inactive (standard SaaS convention: N/A for
// "this doesn't apply here", distinct from Inactive/expired).
const NO_STATUS_MAINTENANCE_TYPES = new Set(["no maintenance", "own maintenance"]);

function maintenanceStatusInfo(plot, maintenanceTypesById) {
  const typeName = (maintenanceTypesById[plot.maintenance_type_id]?.name || plot.maintenance_type || "").trim().toLowerCase();
  if (NO_STATUS_MAINTENANCE_TYPES.has(typeName)) {
    return { text: "N/A", className: "status-na" };
  }
  if (plot.maintenance_validity_date) {
    const validUntil = new Date(plot.maintenance_validity_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (validUntil >= today) {
      return { text: "Active", className: "status-active" };
    }
  }
  return { text: "Inactive", className: "status-inactive" };
}

function errorMessage(err) {
  const detail = err.response?.data?.detail;
  return detail || "Something went wrong. Please try again.";
}

function Plots() {

  const [plots, setPlots] = useState([]);
  const [plotsLoading, setPlotsLoading] = useState(true);
  const [plotsError, setPlotsError] = useState("");

  const [zones, setZones] = useState([]);
  const [projects, setProjects] = useState([]);
  const [waterSupplyTypes, setWaterSupplyTypes] = useState([]);
  const [maintenanceTypes, setMaintenanceTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [staff, setStaff] = useState([]);

  const [searchParams, setSearchParams] = useSearchParams();
  const dueThisMonthFilter = searchParams.get("due") === "this-month";

  const [search, setSearch] = useState("");
  const [zoneFilter, setZoneFilter] = useState("");
  const [supervisorFilter, setSupervisorFilter] = useState("");
  const [maintenanceTypeFilter, setMaintenanceTypeFilter] = useState("");
  // Maintenance-validity due filter — separate from the dashboard's
  // "due this month" quick link above, this lets an admin pick any
  // year/month and see which plots' maintenance validity falls due then.
  const [dueYearFilter, setDueYearFilter] = useState("");
  const [dueMonthFilter, setDueMonthFilter] = useState("");
  const [page, setPage] = useState(1);

  const [expandedId, setExpandedId] = useState(null);
  const [maintenanceHistory, setMaintenanceHistory] = useState({});
  const [maintenanceHistoryLoading, setMaintenanceHistoryLoading] = useState(false);

  useEffect(() => {
    if (!expandedId || maintenanceHistory[expandedId]) return;
    setMaintenanceHistoryLoading(true);
    fetchPlotMaintenanceHistory(expandedId)
      .then((data) => setMaintenanceHistory((prev) => ({ ...prev, [expandedId]: data })))
      .catch(() => setMaintenanceHistory((prev) => ({ ...prev, [expandedId]: [] })))
      .finally(() => setMaintenanceHistoryLoading(false));
  }, [expandedId, maintenanceHistory]);
  const [rowUpdating, setRowUpdating] = useState(null);
  const [rowError, setRowError] = useState("");

  // plotId -> pending (not-yet-confirmed) value typed/selected in the row
  // Defaults to view-only until the backend confirms otherwise — see
  // Settings.ZOHO_UPDATE (these three fields are meant to be Zoho-driven).
  const [zohoUpdateEnabled, setZohoUpdateEnabled] = useState(false);
  const [pendingSupervisor, setPendingSupervisor] = useState({});
  const [pendingOwner, setPendingOwner] = useState({});
  const [pendingZone, setPendingZone] = useState({});

  // =========================
  // LOAD DATA
  // =========================

  const loadPlots = async () => {
    setPlotsLoading(true);
    setPlotsError("");
    try {
      const data = await fetchAssignedPlots();
      setPlots(data);
    } catch (err) {
      setPlotsError(errorMessage(err));
    } finally {
      setPlotsLoading(false);
    }
  };

  useEffect(() => {
    loadPlots();
    fetchZones().then(setZones).catch(() => {});
    fetchProjects().then(setProjects).catch(() => {});
    fetchWaterSupplyTypes().then(setWaterSupplyTypes).catch(() => {});
    fetchMaintenanceTypes().then(setMaintenanceTypes).catch(() => {});
    fetchAllUsers().then(setUsers).catch(() => {});
    fetchStaff().then(setStaff).catch(() => {});
    fetchZohoUpdateStatus().then((r) => setZohoUpdateEnabled(r.enabled)).catch(() => {});
  }, []);

  const projectsById = useMemo(() => Object.fromEntries(projects.map((p) => [p.id, p])), [projects]);
  const waterTypesById = useMemo(() => Object.fromEntries(waterSupplyTypes.map((w) => [w.id, w])), [waterSupplyTypes]);
  const usersById = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const maintenanceTypesById = useMemo(() => Object.fromEntries(maintenanceTypes.map((mt) => [mt.id, mt])), [maintenanceTypes]);
  // Supervisors come from the Staff table (role "supervisor"), not the
  // customer-only Users table used for `customers` below.
  const supervisors = useMemo(() => staff.filter((s) => s.role?.name === "supervisor"), [staff]);
  const customers = useMemo(() => users.filter((u) => u.role?.name === "customer"), [users]);

  const availableDueYears = useMemo(() => {
    const years = new Set();
    for (const plot of plots) {
      if (plot.maintenance_validity_date) years.add(new Date(plot.maintenance_validity_date).getFullYear());
    }
    const currentYear = new Date().getFullYear();
    years.add(currentYear);
    return Array.from(years).sort();
  }, [plots]);

  const zoneLabel = (zone) => zone?.name || zone?.zone_number || "—";


  // =========================
  // FILTER + PAGINATE
  // =========================

  // A customer's plots (e.g. 1A/1B/1C, split from one comma-separated Zoho
  // row) share owner_id but otherwise appear as unrelated rows — group them
  // adjacent in the table instead of scattering by whatever order the API
  // returned, so an admin can see at a glance which rows belong together
  // when setting maintenance per plot.
  const ownerPlotCounts = useMemo(() => {
    const counts = {};
    for (const plot of plots) {
      if (!plot.owner_id) continue;
      counts[plot.owner_id] = (counts[plot.owner_id] || 0) + 1;
    }
    return counts;
  }, [plots]);

  const filteredPlots = useMemo(() => {
    const q = search.trim().toLowerCase();
    return plots
      .filter((plot) => {
        if (dueThisMonthFilter && !plot.maintenance_due_this_month) return false;
        if (zoneFilter && String(plot.zone_id) !== zoneFilter) return false;
        if (supervisorFilter && String(plot.supervisor_id) !== supervisorFilter) return false;
        if (maintenanceTypeFilter && String(plot.maintenance_type_id) !== maintenanceTypeFilter) return false;
        if (dueYearFilter && dueMonthFilter) {
          if (!plot.maintenance_validity_date) return false;
          const validUntil = new Date(plot.maintenance_validity_date);
          if (validUntil.getFullYear() !== Number(dueYearFilter) || validUntil.getMonth() + 1 !== Number(dueMonthFilter)) {
            return false;
          }
        }
        if (q) {
          const owner = plot.owner_name || usersById[plot.owner_id]?.full_name || "";
          const haystack = `${plot.plot_number} ${plot.location} ${owner}`.toLowerCase();
          if (!haystack.includes(q)) return false;
        }
        return true;
      })
      .sort((a, b) => {
        // Unowned plots keep their original relative order at the end —
        // there's nothing to group them by.
        if (!a.owner_id && !b.owner_id) return 0;
        if (!a.owner_id) return 1;
        if (!b.owner_id) return -1;
        if (a.owner_id !== b.owner_id) return a.owner_id - b.owner_id;
        return (a.plot_number || "").localeCompare(b.plot_number || "");
      });
  }, [plots, search, zoneFilter, supervisorFilter, maintenanceTypeFilter, dueYearFilter, dueMonthFilter, usersById, dueThisMonthFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredPlots.length / PAGE_SIZE));
  const pagePlots = filteredPlots.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [search, zoneFilter, supervisorFilter, maintenanceTypeFilter, dueYearFilter, dueMonthFilter]);


  // =========================
  // ROW ACTIONS
  // =========================

  const confirmAssignSupervisor = async (plotId) => {
    const supervisorId = pendingSupervisor[plotId];
    setRowUpdating(plotId);
    setRowError("");
    try {
      const updated = await assignPlotSupervisor(plotId, supervisorId || null);
      setPlots((prev) => prev.map((p) => (p.id === plotId ? updated : p)));
      setPendingSupervisor((prev) => {
        const next = { ...prev };
        delete next[plotId];
        return next;
      });
    } catch (err) {
      setRowError(errorMessage(err));
    } finally {
      setRowUpdating(null);
    }
  };

  const cancelAssignSupervisor = (plotId) => {
    setPendingSupervisor((prev) => {
      const next = { ...prev };
      delete next[plotId];
      return next;
    });
  };

  const confirmAssignOwner = async (plotId) => {
    const ownerId = pendingOwner[plotId];
    setRowUpdating(plotId);
    setRowError("");
    try {
      const updated = await assignPlotOwner(plotId, ownerId || null);
      setPlots((prev) => prev.map((p) => (p.id === plotId ? updated : p)));
      setPendingOwner((prev) => {
        const next = { ...prev };
        delete next[plotId];
        return next;
      });
    } catch (err) {
      setRowError(errorMessage(err));
    } finally {
      setRowUpdating(null);
    }
  };

  const cancelAssignOwner = (plotId) => {
    setPendingOwner((prev) => {
      const next = { ...prev };
      delete next[plotId];
      return next;
    });
  };

  const confirmAssignZone = async (plotId) => {
    const zoneId = pendingZone[plotId];
    setRowUpdating(plotId);
    setRowError("");
    try {
      const updated = await assignPlotZone(plotId, zoneId || null);
      setPlots((prev) => prev.map((p) => (p.id === plotId ? updated : p)));
      setPendingZone((prev) => {
        const next = { ...prev };
        delete next[plotId];
        return next;
      });
    } catch (err) {
      setRowError(errorMessage(err));
    } finally {
      setRowUpdating(null);
    }
  };

  const cancelAssignZone = (plotId) => {
    setPendingZone((prev) => {
      const next = { ...prev };
      delete next[plotId];
      return next;
    });
  };

  return (

    <div>

      <Navbar />

      <Sidebar />

      <div className="admin-content">

        {/* HEADER */}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <h1 style={{ marginTop: "0", marginBottom: 0 }}>Plots</h1>
          <div style={{ display: "flex", gap: "10px" }}>
            <Link to="/zones" className="register-btn" style={{ width: "auto", padding: "10px 20px", textDecoration: "none", display: "inline-flex", alignItems: "center" }}>
              Manage Zones
            </Link>
          </div>
        </div>

        {plotsError && <p className="login-error" style={{ color: "red" }}>{plotsError}</p>}
        {rowError && <p className="login-error" style={{ color: "red" }}>{rowError}</p>}

        {dueThisMonthFilter && (
          <p style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span style={{ background: "#fee2e2", color: "#b91c1c", padding: "4px 10px", borderRadius: "999px", fontWeight: 600 }}>
              Showing plots with maintenance due this month
            </span>
            <button
              type="button"
              className="register-btn"
              style={{ width: "auto", padding: "4px 12px" }}
              onClick={() => setSearchParams({})}
            >
              Clear filter
            </button>
          </p>
        )}

        {/* FILTERS */}

        <div className="filter-row" style={{ display: "flex", gap: "10px", marginBottom: "20px", flexWrap: "wrap" }}>

          <input
            type="text"
            placeholder="Search plot number, location, or owner..."
            className="search-bar"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: "220px" }}
          />

          <select value={zoneFilter} onChange={(e) => setZoneFilter(e.target.value)}>
            <option value="">All Zones</option>
            {zones.map((z) => <option key={z.id} value={z.id}>{zoneLabel(z)}</option>)}
          </select>

          <select value={supervisorFilter} onChange={(e) => setSupervisorFilter(e.target.value)}>
            <option value="">All Supervisors</option>
            {supervisors.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
          </select>

          <select value={maintenanceTypeFilter} onChange={(e) => setMaintenanceTypeFilter(e.target.value)}>
            <option value="">All Maintenance Types</option>
            {maintenanceTypes.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>

          <div style={{ display: "flex", alignItems: "center", gap: "8px", background: "#eef2ff", padding: "4px 10px", borderRadius: "8px" }}>
            <span style={{ fontSize: "13px", fontWeight: 600, color: "#3730a3", whiteSpace: "nowrap" }}>
              Maintenance due in:
            </span>

            <select value={dueMonthFilter} onChange={(e) => setDueMonthFilter(e.target.value)} aria-label="Due month">
              <option value="">Month</option>
              {MONTH_NAMES.map((name, i) => <option key={name} value={i + 1}>{name}</option>)}
            </select>

            <select value={dueYearFilter} onChange={(e) => setDueYearFilter(e.target.value)} aria-label="Due year">
              <option value="">Year</option>
              {availableDueYears.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>

            {(dueYearFilter || dueMonthFilter) && (
              <button
                type="button"
                className="register-btn"
                style={{ width: "auto", padding: "4px 12px" }}
                onClick={() => { setDueYearFilter(""); setDueMonthFilter(""); }}
              >
                Clear
              </button>
            )}
          </div>

        </div>

        {dueYearFilter && !dueMonthFilter && (
          <p style={{ color: "#92400e" }}>Select a month too to see which plots have maintenance due then.</p>
        )}
        {dueMonthFilter && !dueYearFilter && (
          <p style={{ color: "#92400e" }}>Select a year too to see which plots have maintenance due then.</p>
        )}

        {plotsLoading && <p>Loading plots…</p>}

        {!plotsLoading && (
          <p style={{ color: "#666" }}>
            Showing {pagePlots.length} of {filteredPlots.length} plot(s) {filteredPlots.length !== plots.length && `(filtered from ${plots.length} total)`}
          </p>
        )}

        {/* TABLE */}

        {!plotsLoading && (
          <div style={{ background: "white", borderRadius: "10px", overflow: "hidden", boxShadow: "0 0 10px rgba(0,0,0,0.1)" }}>

            <table className="plot-table" style={{ width: "100%", borderCollapse: "collapse" }}>

              <thead style={{ background: "#f5f5f5" }}>
                <tr>
                  <th>Plot</th>
                  <th>Zone</th>
                  <th>Owner</th>
                  <th>Supervisor</th>
                  <th>Area (sqft)</th>
                  <th>Plot Status</th>
                  <th>Maint. Status</th>
                  <th>Water</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>

                {pagePlots.length === 0 && (
                  <tr><td colSpan={9} style={{ padding: "15px" }}>No plots match these filters.</td></tr>
                )}

                {pagePlots.map((plot, i) => {
                  const groupSize = plot.owner_id ? ownerPlotCounts[plot.owner_id] || 1 : 1;
                  const isGrouped = groupSize > 1;
                  const prevPlot = pagePlots[i - 1];
                  const isGroupStart = isGrouped && prevPlot?.owner_id !== plot.owner_id;
                  return (
                  <React.Fragment key={plot.id}>
                    <tr style={isGrouped ? { borderLeft: "3px solid #2e7d32" } : undefined}>
                      <td>
                        <b>{plot.plot_number}</b>
                        {isGroupStart && (
                          <span
                            title="This owner has multiple plots"
                            style={{ marginLeft: "6px", fontSize: "11px", fontWeight: 700, color: "#2e7d32", background: "#e8f5e9", borderRadius: "10px", padding: "2px 8px" }}
                          >
                            {groupSize} plots
                          </span>
                        )}
                      </td>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <select
                            value={plot.id in pendingZone ? (pendingZone[plot.id] || "") : (plot.zone_id || "")}
                            onChange={(e) =>
                              setPendingZone((prev) => ({
                                ...prev,
                                [plot.id]: e.target.value ? Number(e.target.value) : null,
                              }))
                            }
                            disabled={rowUpdating === plot.id}
                          >
                            <option value="">Unassigned</option>
                            {zones.map((z) => <option key={z.id} value={z.id}>{zoneLabel(z)}</option>)}
                          </select>

                          {plot.id in pendingZone && (
                            <>
                              <button
                                type="button"
                                title="Confirm"
                                onClick={() => confirmAssignZone(plot.id)}
                                disabled={rowUpdating === plot.id}
                                style={{ background: "#2e7d32", color: "white", border: "none", borderRadius: "4px", width: "26px", height: "26px", cursor: "pointer" }}
                              >
                                ✓
                              </button>
                              <button
                                type="button"
                                title="Cancel"
                                onClick={() => cancelAssignZone(plot.id)}
                                disabled={rowUpdating === plot.id}
                                style={{ background: "#dc2626", color: "white", border: "none", borderRadius: "4px", width: "26px", height: "26px", cursor: "pointer" }}
                              >
                                ✕
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                      <td>
                        {zohoUpdateEnabled ? (
                          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <select
                              value={plot.id in pendingOwner ? (pendingOwner[plot.id] || "") : (plot.owner_id || "")}
                              onChange={(e) =>
                                setPendingOwner((prev) => ({
                                  ...prev,
                                  [plot.id]: e.target.value ? Number(e.target.value) : null,
                                }))
                              }
                              disabled={rowUpdating === plot.id}
                            >
                              <option value="">
                                {plot.unmatched_owner_name ? `Unmatched: ${plot.unmatched_owner_name}` : "Unassigned"}
                              </option>
                              {customers.map((c) => <option key={c.id} value={c.id}>{c.full_name} ({c.mobile})</option>)}
                            </select>

                            {plot.id in pendingOwner && (
                              <>
                                <button
                                  type="button"
                                  title="Confirm"
                                  onClick={() => confirmAssignOwner(plot.id)}
                                  disabled={rowUpdating === plot.id}
                                  style={{ background: "#2e7d32", color: "white", border: "none", borderRadius: "4px", width: "26px", height: "26px", cursor: "pointer" }}
                                >
                                  ✓
                                </button>
                                <button
                                  type="button"
                                  title="Cancel"
                                  onClick={() => cancelAssignOwner(plot.id)}
                                  disabled={rowUpdating === plot.id}
                                  style={{ background: "#dc2626", color: "white", border: "none", borderRadius: "4px", width: "26px", height: "26px", cursor: "pointer" }}
                                >
                                  ✕
                                </button>
                              </>
                            )}
                          </div>
                        ) : (
                          <span>
                            {plot.owner_name || (plot.unmatched_owner_name ? `Unmatched: ${plot.unmatched_owner_name}` : "Unassigned")}
                          </span>
                        )}
                      </td>

                      <td>
                        {zohoUpdateEnabled ? (
                          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <select
                              value={plot.id in pendingSupervisor ? (pendingSupervisor[plot.id] || "") : (plot.supervisor_id || "")}
                              onChange={(e) =>
                                setPendingSupervisor((prev) => ({
                                  ...prev,
                                  [plot.id]: e.target.value ? Number(e.target.value) : null,
                                }))
                              }
                              disabled={rowUpdating === plot.id}
                            >
                              <option value="">Unassigned</option>
                              {supervisors.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
                            </select>

                            {plot.id in pendingSupervisor && (
                              <>
                                <button
                                  type="button"
                                  title="Confirm"
                                  onClick={() => confirmAssignSupervisor(plot.id)}
                                  disabled={rowUpdating === plot.id}
                                  style={{ background: "#2e7d32", color: "white", border: "none", borderRadius: "4px", width: "26px", height: "26px", cursor: "pointer" }}
                                >
                                  ✓
                                </button>
                                <button
                                  type="button"
                                  title="Cancel"
                                  onClick={() => cancelAssignSupervisor(plot.id)}
                                  disabled={rowUpdating === plot.id}
                                  style={{ background: "#dc2626", color: "white", border: "none", borderRadius: "4px", width: "26px", height: "26px", cursor: "pointer" }}
                                >
                                  ✕
                                </button>
                              </>
                            )}
                          </div>
                        ) : (
                          <span>{plot.supervisor_name || "Unassigned"}</span>
                        )}
                      </td>

                      <td>{plot.area_sqft}</td>

                      <td>
                        <span className={
                          plot.status === "active" ? "status-active" :
                          plot.status === "transferred" ? "status-due" : "status-overdue"
                        }>
                          {plot.status}
                        </span>
                      </td>

                      <td>
                        {/* Computed from maintenance_validity_date (+ maintenance
                            type), not admin-editable — see maintenanceStatusInfo. */}
                        <span className={maintenanceStatusInfo(plot, maintenanceTypesById).className}>
                          {maintenanceStatusInfo(plot, maintenanceTypesById).text}
                        </span>
                      </td>

                      <td>{plot.water_connected ? "✔" : "✖"}</td>

                      <td>
                        <button
                          className="view-btn"
                          onClick={() => setExpandedId(expandedId === plot.id ? null : plot.id)}
                        >
                          {expandedId === plot.id ? "Hide" : "View"}
                        </button>
                      </td>
                    </tr>

                    {expandedId === plot.id && (
                      <tr>
                        <td colSpan={9} style={{ background: "#fafafa", padding: "15px" }}>
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "8px" }}>
                            <p><strong>Project:</strong> {projectsById[plot.project_id]?.name || "—"}</p>
                            <p><strong>Location (legacy):</strong> {plot.location || "—"}</p>
                            <p><strong>Complete Address:</strong> {plot.complete_address || "—"}</p>
                            <p><strong>Plantation Type:</strong> {plot.plantation_type || "—"}</p>
                            <p><strong>Maintenance Type (billing):</strong> {maintenanceTypesById[plot.maintenance_type_id]?.name || plot.maintenance_type || "—"}</p>
                            <p><strong>Maintenance Unit Price:</strong> ₹{plot.maintenance_unit_price ?? "—"}/sqft {plot.area_sqft ? `(₹${(plot.maintenance_unit_price * plot.area_sqft).toLocaleString("en-IN")} per invoice)` : ""}</p>
                            <p><strong>Water Supply Type:</strong> {waterTypesById[plot.water_supply_type_id]?.name || "—"}</p>
                            <p><strong>Electricity Connected:</strong> {plot.electricity_connected ? "Yes" : "No"}</p>
                            <p><strong>Bore Well:</strong> {plot.has_bore_well ? "Yes" : "No"}</p>
                            <p><strong>Water Meter:</strong> {plot.has_water_meter ? "Yes" : "No"}</p>
                            <p><strong>Cottage:</strong> {plot.has_cottage ? "Yes" : "No"}</p>
                            <p><strong>Servant Quarter:</strong> {plot.has_servant_quarter ? "Yes" : "No"}</p>
                            <p><strong>Latitude/Longitude:</strong> {plot.latitude && plot.longitude ? `${plot.latitude}, ${plot.longitude}` : "—"}</p>
                            {plot.co_owner_names?.length > 0 && <p><strong>Co-owners:</strong> {plot.co_owner_names.join(", ")}</p>}
                            <p><strong>Zoho ID:</strong> {plot.zoho_id || "—"}</p>
                            <p><strong>Created:</strong> {new Date(plot.created_at).toLocaleDateString()}</p>
                          </div>

                          <div style={{ marginTop: "15px", borderTop: "1px solid #eee", paddingTop: "10px" }}>
                            <p style={{ fontWeight: 700, marginBottom: "8px" }}>Maintenance History</p>
                            {maintenanceHistoryLoading && !maintenanceHistory[plot.id] && <p>Loading…</p>}
                            {maintenanceHistory[plot.id]?.length === 0 && <p>No maintenance plan changes recorded yet.</p>}
                            {maintenanceHistory[plot.id]?.map((h) => (
                              <div key={h.id} style={{ marginBottom: "8px", paddingBottom: "8px", borderBottom: "1px solid #f0f0f0" }}>
                                <div style={{ display: "flex", justifyContent: "space-between" }}>
                                  <strong>{h.maintenance_type_name || "—"}</strong>
                                  <span style={{ fontSize: "12px", color: "#888" }}>{new Date(h.created_at).toLocaleString()}</span>
                                </div>
                                {h.maintenance_status_name && <p style={{ margin: "2px 0 0", fontSize: "13px", color: "#555" }}>Status: {h.maintenance_status_name}</p>}
                                {h.maintenance_validity_date && <p style={{ margin: "2px 0 0", fontSize: "13px", color: "#555" }}>Valid until: {new Date(h.maintenance_validity_date).toLocaleDateString()}</p>}
                                {h.maintenance_due_amount != null && <p style={{ margin: "2px 0 0", fontSize: "13px", color: "#555" }}>Due: ₹{h.maintenance_due_amount}</p>}
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  );
                })}

              </tbody>

            </table>

          </div>
        )}

        {!plotsLoading && totalPages > 1 && (
          <div style={{ display: "flex", gap: "10px", alignItems: "center", marginTop: "15px" }}>
            <button className="register-btn" style={{ width: "auto", padding: "6px 14px" }} onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
              Previous
            </button>
            <span>Page {page} of {totalPages}</span>
            <button className="register-btn" style={{ width: "auto", padding: "6px 14px" }} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
              Next
            </button>
          </div>
        )}

      </div>

    </div>

  );

}

export default Plots;
